/**
 * Multi-account store backed by localStorage.
 *
 * Each login is persisted as a {@link StoredAccount} — a display username plus
 * the full token response — so the user can keep several Kobiton logins on hand
 * and switch between them from the devices page without re-authenticating.
 *
 * One account is marked "active"; that is the token the app actually uses.
 */

import { type TokenResponse } from './oauth';
import { DEFAULT_API_URL, normalizeApiUrl } from './environment';

const ACCOUNTS_KEY = 'kobiton.accounts';
const ACTIVE_ACCOUNT_KEY = 'kobiton.active_account';

export interface StoredAccount {
  /** Stable identity for the account (JWT `sub`/email when available). */
  id: string;
  /** Identity derived from token claims; separate from the local storage id. */
  identityId?: string;
  /** Human-friendly label shown in the account switcher. */
  username: string;
  /** The stored token, used to authenticate the realtime socket. */
  token: TokenResponse;
  /** API environment used to authenticate and connect this account. */
  baseApiUrl: string;
  /** When this account was first saved (unix epoch ms). */
  addedAt: number;
}

/** Decode a JWT payload without verifying the signature (browser-safe). */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const payloadPart = jwt.split('.')[1];
  if (!payloadPart) {
    return null;
  }
  try {
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const parsed = JSON.parse(json) as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function firstString(source: Record<string, unknown> | null, keys: string[]): string | null {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

/**
 * Identity hints from a token's JWT claims. Kobiton's tokens carry no user
 * info, so both fields are usually `null` — callers supply a unique id and a
 * default name in that case (and the user can rename afterwards).
 */
function deriveIdentity(token: TokenResponse): { id: string | null; username: string | null } {
  const claims = decodeJwtPayload(token.access_token);
  const username = firstString(claims, ['email', 'preferred_username', 'username', 'name']);
  const id = firstString(claims, ['sub', 'email', 'username']);
  return { id, username };
}

/** Unique id for an account that carries no identifying claim. */
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `acct-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readAccounts(): StoredAccount[] {
  const raw = localStorage.getItem(ACCOUNTS_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (item): item is StoredAccount =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as StoredAccount).id === 'string' &&
        typeof (item as StoredAccount).username === 'string' &&
        typeof (item as StoredAccount).token === 'object' &&
        (item as StoredAccount).token !== null,
    ).map((account) => ({
      ...account,
      // Accounts saved before environment selection was added used production.
      baseApiUrl:
        typeof (account as Partial<StoredAccount>).baseApiUrl === 'string'
          ? normalizeApiUrl((account as StoredAccount).baseApiUrl)
          : DEFAULT_API_URL,
    }));
  } catch {
    return [];
  }
}

function writeAccounts(accounts: StoredAccount[]): void {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

/** All stored accounts, oldest first. */
export function listAccounts(): StoredAccount[] {
  return readAccounts();
}

/** The id of the account whose token the app is currently using. */
export function getActiveAccountId(): string | null {
  return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
}

/** The active account, or `null` if none is stored/selected. */
export function getActiveAccount(): StoredAccount | null {
  const accounts = readAccounts();
  const activeId = getActiveAccountId();
  return accounts.find((a) => a.id === activeId) ?? accounts[0] ?? null;
}

/**
 * Persist a freshly obtained token as an account and mark it active. If an
 * account with the same identity already exists, its token is refreshed in
 * place rather than duplicated.
 */
export function saveAccount(token: TokenResponse, baseApiUrl = DEFAULT_API_URL): StoredAccount {
  const { id: claimId, username: claimUsername } = deriveIdentity(token);
  const accounts = readAccounts();
  const normalizedApiUrl = normalizeApiUrl(baseApiUrl);
  const existing = claimId
    ? accounts.find((a) => (a.identityId ?? a.id) === claimId && a.baseApiUrl === normalizedApiUrl)
    : undefined;
  const availableClaimId = claimId && !accounts.some((a) => a.id === claimId) ? claimId : null;

  const account: StoredAccount = {
    id: existing?.id ?? availableClaimId ?? generateId(),
    identityId: claimId ?? existing?.identityId,
    // Keep a name the user may have set; otherwise use the claim, then a
    // numbered default so multiple claimless accounts stay distinguishable.
    username: existing?.username ?? claimUsername ?? `Account ${accounts.length + 1}`,
    token,
    baseApiUrl: normalizedApiUrl,
    addedAt: existing?.addedAt ?? Date.now(),
  };

  const next = existing ? accounts.map((a) => (a.id === account.id ? account : a)) : [...accounts, account];
  writeAccounts(next);
  localStorage.setItem(ACTIVE_ACCOUNT_KEY, account.id);
  return account;
}

/** Set a user-chosen display name for an account. Empty names are ignored. */
export function renameAccount(id: string, username: string): void {
  const trimmed = username.trim();
  if (!trimmed) {
    return;
  }
  const accounts = readAccounts().map((a) => (a.id === id ? { ...a, username: trimmed } : a));
  writeAccounts(accounts);
}

/** Mark a stored account as the active one. No-op if the id is unknown. */
export function setActiveAccount(id: string): void {
  if (readAccounts().some((a) => a.id === id)) {
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, id);
  }
}

/**
 * Remove an account from the store. If the removed account was active, the
 * active selection falls back to the first remaining account (or is cleared
 * when none remain).
 */
export function removeAccount(id: string): void {
  const remaining = readAccounts().filter((a) => a.id !== id);
  writeAccounts(remaining);

  if (getActiveAccountId() === id) {
    const fallback = remaining[0];
    if (fallback) {
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, fallback.id);
    } else {
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    }
  }
}
